import { HttpClient, HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from "@angular/router";
import { BehaviorSubject, catchError, EMPTY, filter, map, switchMap, take, throwError } from 'rxjs';
import { AuthApiService } from '../services/auth-api.service';
import { SessionService } from '../services/session.service';
import { ILoginSuccessResponse } from '../models/auth.models';
import { JwtUtil } from '@ribera-web/token-decodifier-service';
import { SKIP_AUTH } from '@ribera-web/app-token';
import { AuthServerClockService } from '../services/auth-server-clock.service';

// Manejar multiple refresh
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const sessionService = inject(SessionService);
    const authService = inject(AuthApiService);
    const jwtUtil = inject(JwtUtil);
    const clock = inject(AuthServerClockService);

    if (req.context.get(SKIP_AUTH)) {
        return next(req);
    }

    const isGoogleRequest =
        req.url.includes('accounts.google.com') ||
        req.url.includes('googleapis.com');

    // Caso 1 - Validar petición GOOGLE
    if (isGoogleRequest) {
        return next(req);
    }

    const token = localStorage.getItem('accessToken');

    const isValidationRequest = req.url.includes('/auth/refresh');

    // Caso 2 - refresh TOKEN
    if (isValidationRequest) {
        return next(req);
    }

    // Caso 3 - Validar si existe TOKEN
    if (!token) {
        return next(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    sessionService.logout();
                    router.navigate(['/auth/login']);
                }
                return throwError(() => error);
            })
        );
    }

    // Datos de expiración
    let exp = 0;
    try {
        exp = jwtUtil.getExpirationDate(token) * 1000;
    } catch (error) {
        console.error('Error decoding token in interceptor', error);
        sessionService.logout();
        router.navigate(['/auth/login']);
        return throwError(() => new Error('Invalid token'));
    }
    const now = clock.nowMs();

    // Caso 4 - Expiration uses synchronized server time, not the device clock.
    const isExpired = now !== null && now >= exp;
    if (isExpired) {
        sessionService.logout();
        router.navigate(['/auth/login']);
        return throwError(() => new Error('Session expired'));
    }

    // Preparación de datos
    const isFormData = req.body instanceof FormData;
    const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
    };

    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    const authReq = req.clone({ setHeaders: headers });

    // Caso 5 - Si la expiración en mayor a 5 minutos
    // No clock yet (e.g. reload): refresh through Security MS to revalidate.
    const remainingTime = now === null ? 0 : exp - now;
    if (remainingTime > 5 * 60 * 1000) {
        return next(authReq);
    }

    // Caso 6 - Refresh Token: Petición cumpliendo condiciones
    // Condiciones: Token válido, Token a con menos de 5 minutos para expirar
    if (!isRefreshing) {
        isRefreshing = true;
        refreshSubject.next(null);

        return authService.refreshToken().pipe(
            switchMap((response: ILoginSuccessResponse) => {
                const newTokens = response.data!;
                isRefreshing = false;

                if (!newTokens?.access_token || !response.timestamp) {
                    throw new Error('Invalid refresh response');
                }
                clock.synchronize(response.timestamp);
                if (clock.isExpired(jwtUtil.getExpirationDate(newTokens.access_token)) !== false) {
                    throw new Error('Expired refreshed token');
                }
                localStorage.setItem('accessToken', newTokens.access_token);
                sessionService.startSessionTimer(newTokens.access_token);

                refreshSubject.next(newTokens.access_token);

                const newAuthReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${newTokens.access_token}`,
                        ...(!isFormData && { 'Content-Type': 'application/json' })
                    }
                });
                return next(newAuthReq);
            }),
            catchError((error: HttpErrorResponse) => {
                isRefreshing = false;
                sessionService.logout();
                router.navigate(['/auth/login']);
                return throwError(() => error);
            })
        );
    } else {
        // Esperar a que el refresh en curso termine
        return refreshSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(newToken => {
                const newAuthReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${newToken}`,
                        ...(!isFormData && { 'Content-Type': 'application/json' })
                    }
                });
                return next(newAuthReq);
            })
        );
    }
};
